import { useEffect, useRef, useState } from "react";
import { WifiOff, Wifi, X } from "lucide-react";
import { useOnlineStatus } from "../hooks/useOnlineStatus";

type OfflineBannerProps = {
  title?: string;
  message?: string;
  bannerMessage?: string;
  pillText?: string;
  showTopBanner?: boolean;
  showPill?: boolean;
  showModal?: boolean;
  pillOffsetClass?: string;
  /**
   * When true, registers a `beforeunload` handler while offline so the
   * browser asks for confirmation before refresh / tab close. The visual
   * banners are usually enough — only opt in for screens where an
   * accidental refresh would lose user work. Default: false.
   */
  warnBeforeUnload?: boolean;
};

export function OfflineBanner({
  title = "You're Offline",
  message = "Some features may be unavailable. Please check your internet connection.",
  bannerMessage = "You're offline. Some features may be unavailable.",
  pillText = "Disconnected",
  showTopBanner = true,
  showPill = true,
  showModal = true,
  pillOffsetClass = "top-12",
  warnBeforeUnload = false,
}: OfflineBannerProps) {
  const { isOnline } = useOnlineStatus();
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [showConnectedToast, setShowConnectedToast] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);
  const connectedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!wasOffline && !isOnline) {
      // Auto-open the modal whenever we detect offline state — both on a
      // live online → offline transition and on a cached refresh where the
      // page loaded while already offline.
      if (showModal) {
        setShowOfflineModal(true);
      }
      setWasOffline(true);
    } else if (wasOffline && isOnline) {
      setShowOfflineModal(false);
      setShowConnectedToast(true);
      setWasOffline(false);

      if (connectedTimeoutRef.current) {
        clearTimeout(connectedTimeoutRef.current);
      }
      connectedTimeoutRef.current = setTimeout(() => {
        setShowConnectedToast(false);
      }, 3000);
    }
  }, [isOnline, wasOffline, showModal]);

  useEffect(() => {
    return () => {
      if (connectedTimeoutRef.current) {
        clearTimeout(connectedTimeoutRef.current);
      }
    };
  }, []);

  // Opt-in only. Asks the browser to confirm before refresh / tab close
  // while offline so the user doesn't accidentally land on the native
  // offline page (Chrome dinosaur). Most screens don't need this — the
  // visual banner already informs the user.
  useEffect(() => {
    if (!warnBeforeUnload || isOnline) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isOnline, warnBeforeUnload]);

  // Block all user interactions (click, form submit, Enter/Space activation)
  // while offline so actions that would silently fail never reach handlers.
  // Elements inside this component (marked with [data-offline-ui]) are
  // allowed so the user can still dismiss the modal/toast.
  useEffect(() => {
    if (isOnline) return;

    const isAllowed = (target: EventTarget | null): boolean =>
      target instanceof Element && target.closest("[data-offline-ui]") !== null;

    const revealOfflineModal = () => {
      if (showModal) setShowOfflineModal(true);
    };

    const clickBlocker = (e: MouseEvent) => {
      if (isAllowed(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      revealOfflineModal();
    };

    const submitBlocker = (e: SubmitEvent) => {
      if (isAllowed(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      revealOfflineModal();
    };

    const keydownBlocker = (e: KeyboardEvent) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const target = e.target as HTMLElement | null;
      if (!target || isAllowed(target)) return;

      // Let text typing through (text input + textarea + contenteditable)
      const tag = target.tagName;
      if (tag === "TEXTAREA") return;
      if (tag === "INPUT") {
        const type = (target as HTMLInputElement).type?.toLowerCase();
        const activatable = type === "submit" || type === "button" || type === "checkbox" || type === "radio";
        if (!activatable) return;
      }
      if (target.isContentEditable) return;

      e.preventDefault();
      e.stopPropagation();
      revealOfflineModal();
    };

    document.addEventListener("click", clickBlocker, { capture: true });
    document.addEventListener("submit", submitBlocker, { capture: true });
    document.addEventListener("keydown", keydownBlocker, { capture: true });
    return () => {
      document.removeEventListener("click", clickBlocker, { capture: true });
      document.removeEventListener("submit", submitBlocker, { capture: true });
      document.removeEventListener("keydown", keydownBlocker, { capture: true });
    };
  }, [isOnline, showModal]);

  return (
    <>
      {!isOnline && showTopBanner && (
        <div
          role="alert"
          data-offline-ui
          className="fixed top-0 left-0 right-0 z-[60] bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-center gap-2 text-sm text-amber-800 shadow-sm"
        >
          <WifiOff size={16} className="text-amber-600 shrink-0" />
          <span className="font-medium">{bannerMessage}</span>
        </div>
      )}

      {!isOnline && showPill && (
        <div
          data-offline-ui
          className={`fixed ${pillOffsetClass} right-4 z-40 pointer-events-none`}
        >
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-lg bg-amber-100 text-amber-700 border border-amber-200 text-xs font-medium">
            <WifiOff size={12} className="text-amber-600" />
            <span>{pillText}</span>
          </div>
        </div>
      )}

      {!isOnline && showModal && showOfflineModal && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="offline-modal-title"
          data-offline-ui
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowOfflineModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowOfflineModal(false)}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={18} className="text-gray-500" />
            </button>

            <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
              <WifiOff size={32} className="text-amber-600" />
            </div>

            <div className="text-center mb-6">
              <h3
                id="offline-modal-title"
                className="text-xl font-bold text-gray-900 mb-2"
              >
                {title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">{message}</p>
            </div>

            <button
              type="button"
              onClick={() => setShowOfflineModal(false)}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {isOnline && showConnectedToast && (
        <div
          role="status"
          aria-live="polite"
          data-offline-ui
          className="fixed top-0 left-0 right-0 z-[70]"
        >
          <div className="bg-green-50 border-b border-green-200 px-4 py-3 flex items-center justify-center gap-3">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shrink-0">
              <Wifi size={16} className="text-white" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-green-900">
                You&apos;re back online!
              </span>
              <span className="text-xs text-green-600">
                All features are now available
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowConnectedToast(false)}
              className="ml-2 p-1 rounded-full hover:bg-green-100 transition-colors"
              aria-label="Dismiss"
            >
              <X size={16} className="text-green-600" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
